import { keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import i18n from '@ohif/i18n';

import { apiClient } from '../../apiHelper';
import { useFetch } from '../react-query';

interface ApiResponse<T> {
  data: T;
}

const TOAST_NS = 'Toast';

function toastError(key: string) {
  const text = i18n.t(key, { ns: TOAST_NS, defaultValue: key });
  message.error(text);
}

export type DiscussionType = 'COMMENT' | 'OPINION_REQUEST' | 'REPLY';
export type DiscussionRequestIntent = 'READ_ONLY' | 'REVIEW';
export type DiscussionStatus = 'OPEN' | 'ANSWERED' | 'CLOSED';
export type DiscussionTargetType = 'STUDY' | 'SERIE' | 'FRAME' | 'REPORT';
export type AnswerConclusion = 'AGREE' | 'DISAGREE' | 'UNCERTAIN' | 'NEED_MORE_INFO';

export interface StructuredAnswer {
  conclusion?: AnswerConclusion;
  assessment?: string;
  recommendation?: string | null;
}

export interface AnswerSuggestionVariant {
  answer: StructuredAnswer;
  suggested_content: string;
}

export interface AnswerSuggestion {
  suggestions: AnswerSuggestionVariant[];
  disclaimer: string;
  model: string;
  generated_at: string;
}

export interface DiscussionConfig {
  llm_suggest_enabled: boolean;
}

export interface AuthorSummary {
  id: string;
  email: string;
  role: string;
  display_name: string;
}

export interface Discussion {
  id: string;
  discussion_type: DiscussionType;
  request_intent?: DiscussionRequestIntent | null;
  parent_id?: string | null;
  root_id?: string | null;
  content: string;
  status?: DiscussionStatus | null;
  target_type: DiscussionTargetType;
  target_ref: string;
  target_label?: string | null;
  target_context?: {
    view_name?: string | null;
    view_code?: string | null;
    frame_number?: number | null;
    sop_instance_uid?: string | null;
    serie_instance_uid?: string | null;
    serie_type?: string | null;
  } | null;
  study_instance_uid: string;
  patient_id?: string | null;
  official_answer_id?: string | null;
  answer_payload?: StructuredAnswer | null;
  author: AuthorSummary;
  recipients: Array<{ user_id: string; display_name: string; responded_at?: string | null }>;
  reply_count: number;
  last_reply_at?: string | null;
  last_reply_author?: string | null;
  unread_for_current_user: boolean;
  created_at: string;
  updated_at?: string | null;
  replies?: Discussion[];
}

export interface DiscussionListResponse {
  data: Discussion[];
  total_count: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export interface DiscussionSummary {
  total_threads: number;
  open_opinion_requests: number;
  unread_for_current_user: number;
}

export interface Colleague {
  id: string;
  email: string;
  role: string;
  display_name: string;
}

export interface CreateDiscussionPayload {
  request_intent: DiscussionRequestIntent;
  discussion_type: 'COMMENT' | 'OPINION_REQUEST';
  content: string;
  target_type: DiscussionTargetType;
  target_ref: string;
  study_instance_uid: string;
  recipient_user_ids?: string[];
}

export interface ReplyDiscussionPayload {
  content: string;
  answer?: StructuredAnswer;
}

export const discussionsQueryKeys = {
  all: ['discussions'] as const,
  config: ['discussions', 'config'] as const,
  summary: (studyInstanceUid: string) =>
    ['discussions', 'summary', studyInstanceUid] as const,
  list: (studyInstanceUid: string) =>
    ['discussions', 'list', studyInstanceUid] as const,
  thread: (discussionId: string) =>
    ['discussions', 'thread', discussionId] as const,
  colleagues: ['discussions', 'colleagues'] as const,
};

const fetchConfig = {
  placeholderData: keepPreviousData,
  staleTime: 15_000,
  refetchOnWindowFocus: true,
};

export function useDiscussionConfig(enabled = true) {
  const fetchDiscussionConfig = async (): Promise<DiscussionConfig> => {
    const response = (await apiClient.get('/discussions/config')) as ApiResponse<
      DiscussionConfig
    >;
    return response?.data ?? { llm_suggest_enabled: false };
  };

  return useFetch(fetchDiscussionConfig, [...discussionsQueryKeys.config], {
    enabled,
    staleTime: 60_000,
  });
}

export function useDiscussionSummary(studyInstanceUid?: string) {
  const fetchSummary = async (): Promise<DiscussionSummary | null> => {
    if (!studyInstanceUid) return null;
    const response = (await apiClient.get(
      `/discussions/summary/${encodeURIComponent(studyInstanceUid)}`
    )) as ApiResponse<DiscussionSummary>;
    return response?.data ?? null;
  };

  return useFetch(
    fetchSummary,
    studyInstanceUid ? [...discussionsQueryKeys.summary(studyInstanceUid)] : null,
    { enabled: Boolean(studyInstanceUid), ...fetchConfig }
  );
}

export type DiscussionListFilters = {
  targetType?: DiscussionTargetType;
  targetRef?: string;
};

function normalizeTargetRef(ref: string): string {
  return ref.trim().toLowerCase();
}

export function discussionMatchesLocation(
  discussion: Discussion,
  targetType: DiscussionTargetType,
  targetRef: string
): boolean {
  return (
    discussion.target_type === targetType &&
    normalizeTargetRef(discussion.target_ref) === normalizeTargetRef(targetRef)
  );
}

export function resolveRequestIntent(
  discussion: Pick<Discussion, 'request_intent' | 'discussion_type'>
): DiscussionRequestIntent {
  if (discussion.request_intent) return discussion.request_intent;
  return discussion.discussion_type === 'OPINION_REQUEST' ? 'REVIEW' : 'READ_ONLY';
}

export function useDiscussions(
  studyInstanceUid?: string,
  filters?: DiscussionListFilters,
  enabled = true
) {
  const isFiltered = Boolean(filters?.targetType || filters?.targetRef);

  const fetchList = async (): Promise<DiscussionListResponse | null> => {
    if (!studyInstanceUid) return null;
    const params = new URLSearchParams({ study_instance_uid: studyInstanceUid });
    if (filters?.targetType) params.set('target_type', filters.targetType);
    if (filters?.targetRef) params.set('target_ref', filters.targetRef);
    const response = (await apiClient.get(
      `/discussions?${params.toString()}`
    )) as ApiResponse<DiscussionListResponse>;
    return response?.data ?? null;
  };

  const queryKey = studyInstanceUid
    ? [
        ...discussionsQueryKeys.list(studyInstanceUid),
        filters?.targetType ?? 'all',
        filters?.targetRef ?? 'all',
      ]
    : null;

  return useFetch(fetchList, queryKey, {
    enabled: Boolean(studyInstanceUid) && enabled,
    staleTime: isFiltered ? 0 : fetchConfig.staleTime,
    refetchOnMount: isFiltered ? 'always' : true,
    refetchOnWindowFocus: fetchConfig.refetchOnWindowFocus,
    placeholderData: isFiltered ? undefined : keepPreviousData,
  });
}

export function markDiscussionReadInCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  studyInstanceUid: string,
  discussionId: string
) {
  queryClient.setQueriesData<DiscussionListResponse | null>(
    { queryKey: discussionsQueryKeys.list(studyInstanceUid) },
    (old) => {
      if (!old?.data) return old;
      const hasUnread = old.data.some(
        (d) => d.id === discussionId && d.unread_for_current_user
      );
      if (!hasUnread) return old;
      return {
        ...old,
        data: old.data.map((d) =>
          d.id === discussionId ? { ...d, unread_for_current_user: false } : d
        ),
      };
    }
  );

  void queryClient.invalidateQueries({
    queryKey: discussionsQueryKeys.summary(studyInstanceUid),
  });
}

export function useDiscussionThread(
  discussionId?: string,
  studyInstanceUid?: string
) {
  const queryClient = useQueryClient();

  const fetchThread = async (): Promise<Discussion | null> => {
    if (!discussionId) return null;
    const response = (await apiClient.get(
      `/discussions/${discussionId}`
    )) as ApiResponse<Discussion>;
    const thread = response?.data ?? null;
    if (thread && studyInstanceUid) {
      markDiscussionReadInCaches(queryClient, studyInstanceUid, discussionId);
    }
    return thread;
  };

  return useFetch(
    fetchThread,
    discussionId ? [...discussionsQueryKeys.thread(discussionId)] : null,
    {
      enabled: Boolean(discussionId),
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: fetchConfig.refetchOnWindowFocus,
    }
  );
}

export function useClinicalColleagues(enabled = false) {
  const fetchColleagues = async (): Promise<Colleague[]> => {
    const response = (await apiClient.get(
      '/discussions/colleagues'
    )) as ApiResponse<Colleague[]>;
    return response?.data ?? [];
  };

  return useFetch(fetchColleagues, [...discussionsQueryKeys.colleagues], {
    enabled,
    staleTime: 60_000,
  });
}

function listCacheMatchesItem(
  queryKey: readonly unknown[],
  item: Discussion
): boolean {
  const filterType = queryKey[3];
  const filterRef = queryKey[4];
  if (filterType !== 'all' && filterType !== item.target_type) return false;
  if (filterRef !== 'all' && filterRef !== item.target_ref) return false;
  return true;
}

function prependToListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  studyInstanceUid: string,
  item: Discussion
) {
  const entries = queryClient.getQueriesData<DiscussionListResponse | null>({
    queryKey: discussionsQueryKeys.list(studyInstanceUid),
  });

  for (const [queryKey] of entries) {
    if (!listCacheMatchesItem(queryKey, item)) continue;
    queryClient.setQueryData<DiscussionListResponse | null>(queryKey, (old) => {
      if (!old?.data) return old;
      if (old.data.some((d) => d.id === item.id)) return old;
      return {
        ...old,
        data: [item, ...old.data],
        total_count: old.total_count + 1,
      };
    });
  }
}

function appendReplyToThreadCache(
  queryClient: ReturnType<typeof useQueryClient>,
  rootId: string,
  reply: Discussion
) {
  queryClient.setQueryData<Discussion | null>(
    [...discussionsQueryKeys.thread(rootId)],
    (old) => {
      if (!old) return old;
      if ((old.replies ?? []).some((r) => r.id === reply.id)) return old;
      const nextStatus =
        reply.answer_payload && old.status === 'OPEN' ? 'ANSWERED' : old.status;
      return {
        ...old,
        status: nextStatus,
        official_answer_id: reply.answer_payload
          ? reply.id
          : old.official_answer_id,
        replies: [...(old.replies ?? []), reply],
        reply_count: (old.reply_count ?? 0) + 1,
        last_reply_at: reply.created_at,
        last_reply_author: reply.author?.display_name,
      };
    }
  );

  queryClient.setQueriesData<DiscussionListResponse | null>(
    { queryKey: ['discussions', 'list'] },
    (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((d) =>
          d.id === rootId
            ? {
                ...d,
                status:
                  reply.answer_payload && d.status === 'OPEN' ? 'ANSWERED' : d.status,
                reply_count: (d.reply_count ?? 0) + 1,
                last_reply_at: reply.created_at,
                last_reply_author: reply.author?.display_name,
              }
            : d
        ),
      };
    }
  );
}

export function useCreateDiscussion(studyInstanceUid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDiscussionPayload) => {
      const response = (await apiClient.post('/discussions', payload)) as ApiResponse<
        Discussion
      >;
      return response?.data;
    },
    onSuccess: (created) => {
      if (created) {
        prependToListCaches(queryClient, studyInstanceUid, created);
      }
      void queryClient.invalidateQueries({
        queryKey: discussionsQueryKeys.summary(studyInstanceUid),
      });
    },
    onError: () => {
      toastError('clinicalCollaboration.createFailed');
    },
  });
}

export function useUpdateDiscussionStatus(
  studyInstanceUid: string,
  discussionId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (status: 'OPEN' | 'CLOSED') => {
      const response = (await apiClient.patch(
        `/discussions/${discussionId}/status`,
        { status }
      )) as ApiResponse<Discussion>;
      return response?.data;
    },
    onSuccess: (updated) => {
      if (updated && discussionId) {
        queryClient.setQueryData<Discussion | null>(
          [...discussionsQueryKeys.thread(discussionId)],
          (old) => (old ? { ...old, status: updated.status } : old)
        );
        queryClient.setQueriesData<DiscussionListResponse | null>(
          { queryKey: discussionsQueryKeys.list(studyInstanceUid) },
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((d) =>
                d.id === discussionId ? { ...d, status: updated.status } : d
              ),
            };
          }
        );
      }
      void queryClient.invalidateQueries({
        queryKey: discussionsQueryKeys.summary(studyInstanceUid),
      });
    },
    onError: () => {
      toastError('clinicalCollaboration.statusFailed');
    },
  });
}

export function useReplyDiscussion(studyInstanceUid: string, discussionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ReplyDiscussionPayload) => {
      const response = (await apiClient.post(
        `/discussions/${discussionId}/replies`,
        payload
      )) as ApiResponse<Discussion>;
      return response?.data;
    },
    onSuccess: (reply) => {
      if (reply && discussionId) {
        appendReplyToThreadCache(queryClient, discussionId, reply);
      }
      void queryClient.invalidateQueries({
        queryKey: discussionsQueryKeys.summary(studyInstanceUid),
      });
    },
    onError: () => {
      toastError('clinicalCollaboration.replyFailed');
    },
  });
}

export function useSuggestAnswer(discussionId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = (await apiClient.post(
        `/discussions/${discussionId}/suggest-answer`
      )) as ApiResponse<AnswerSuggestion>;
      return response?.data;
    },
    onSuccess: () => {
      const text = i18n.t('clinicalCollaboration.suggestReady', {
        ns: TOAST_NS,
        defaultValue: 'Draft ready — review and edit before posting',
      });
      message.success(text);
    },
    onError: () => {
      toastError('clinicalCollaboration.suggestFailed');
    },
  });
}
